import { useAuth } from '@agentnet/auth';
import { pxToRem } from '@fluentsms/agentnet-web-components';
import { Grid, IconButton } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { AgentNetTextInput } from 'ui-kit/inputs';
import { deleteEmailNotification } from '../../../api/admin/user-profile-api';

interface emailType {
  ServiceOrderEmailNotificationId: number;
  ServiceOrderEmailAddress: string;
}

export interface SearchOrderEmailsTableRef {
  addEmail: () => void;
}

interface SearchOrderEmailsTableProps {
  emails?: emailType[];
  disabled?: boolean;
  onDelete?: (index: any) => void;
  onChange?: (items: any) => void;
  qaBtnprefix?: string;
  isDuplicate: (index: any) => void;
  onValidate: (index: any) => void;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    emailInputCell: {
      display: 'flex',
      alignItems: 'center',
    },
    removeIcon: {
      padding: 0,
      marginLeft: theme.spacing(1),
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
    validationMsg: {
      width: '100%',
      fontSize: pxToRem(14),
    },
    validationMsgError: {
      width: '100%',
      fontSize: pxToRem(14),
      color: theme.palette.error.main,
    },
  }),
);

const SearchOrderEmailsTable = forwardRef<SearchOrderEmailsTableRef, SearchOrderEmailsTableProps>(
  ({ disabled, emails, onChange, onDelete, qaBtnprefix, isDuplicate, onValidate }, ref) => {
    const classes = useStyles();

    const { getAccessToken } = useAuth();
    const [items, setItems] = useState<emailType[]>(emails ?? []);
    const newEmailDetails: emailType = { ServiceOrderEmailNotificationId: 0, ServiceOrderEmailAddress: '' };
    const [isduplicate, setIsDuplicate] = useState<boolean>(false);
    const [showmessage, setShowmessage] = useState<boolean>(false);
    const [emailFormatValidation, setEmailFormatValidation] = useState<string>('');
    const [validateEmailResult, setvalidateEmailResult] = useState<boolean>();

    useEffect(() => {
      if (emails) {
        setItems(emails);
      }
    }, [emails]);

    useEffect(() => {
      onValidate(validateEmailResult);
    }, [validateEmailResult]);

    useEffect(() => {
      isDuplicate(isduplicate);
    }, [isduplicate]);

    const addNewEmailField = () => {
      const nextItems = [...items, newEmailDetails];
      setItems(nextItems);
      onChange?.(nextItems);
    };

    useImperativeHandle(ref, () => ({
      addEmail: addNewEmailField,
    }));

    const handleEmailChange = (val: string, i: number) => {
      const newEmails = [...items];
      setEmailFormatValidation(val);
      setIsDuplicate(newEmails.some((item) => item.ServiceOrderEmailAddress === val));
      newEmails[i].ServiceOrderEmailAddress = val;
      setItems(newEmails);
      onChange?.(newEmails);
    };

    const handleDelete = (i: number) => {
      const newEmails = items.filter((_, index) => index !== i);
      setvalidateEmailResult(true);
      setItems(newEmails);
      onChange?.(newEmails);
      //set the value as false when delete happens
      setIsDuplicate(false);
      const deletedEmail = items[i];
      onDelete && onDelete(i);
      DeleteServiceOrderEmailNotification(deletedEmail?.ServiceOrderEmailNotificationId);
    };

    const DeleteServiceOrderEmailNotification = async (id: number) => {
      const token = await getAccessToken();
      await deleteEmailNotification(id, token);
    };

    const validateEmail = (item: string): boolean => {
      const regex = /^\b[A-Z0-9._%-]+@[A-Z0-9.-]+\.[A-Z]{2,4}\b$/i;
      if (item !== '') return regex.test(item);
      else return true;
    };
    //function executes when grid focus is out
    const handleBlur = () => {
      setShowmessage(true);
      setvalidateEmailResult(validateEmail(emailFormatValidation));
    };

    return (
      <>
        {items.length === 0 && (
          <Grid item sm={12}>
            <div className={classes.validationMsg}>There are no Service Order Email Notifications.</div>
          </Grid>
        )}
        {isduplicate && items.length !== 0 && (
          <Grid item sm={12}>
            <div className={classes.validationMsgError}>
              Email is already set up. Please enter a unique email address.
            </div>
          </Grid>
        )}
        {!validateEmailResult && items.length > 0 && showmessage && (
          <Grid item sm={12}>
            <div className={classes.validationMsgError}>Please enter a valid Email address</div>
          </Grid>
        )}
        {items.map((e: emailType, i) => (
          <Grid item sm={3} key={i} className={classes.emailInputCell}>
            <AgentNetTextInput
              fullWidth
              variant="outlined"
              value={e.ServiceOrderEmailAddress}
              data-testid={qaBtnprefix + 'Field'}
              onChange={(event) => handleEmailChange(event.target.value, i)}
              onBlur={handleBlur}
              name="ServiceorderNotificationEmail"
            />
            {onDelete && (
              <IconButton
                className={classes.removeIcon}
                disableFocusRipple
                disableRipple
                disableTouchRipple
                aria-label="remove email notification"
                data-testid={qaBtnprefix + 'Close' + i}
                onClick={() => handleDelete(i)}
                disabled={disabled}
              >
                <RemoveCircleOutlineIcon color="disabled" />
              </IconButton>
            )}
          </Grid>
        ))}
      </>
    );
  },
);

SearchOrderEmailsTable.displayName = 'SearchOrderEmailsTable';

export default SearchOrderEmailsTable;
